/**
 * 몬스터 도트 아트 생성.
 * 원작 몬스터의 실루엣과 색감(둥근 몸, 굵은 외곽선, 큰 눈)을 참고해 직접 그렸다.
 * 모든 몬스터는 오른쪽을 보는 기준으로 그리고, 왼쪽으로 갈 때는 flipX 로 처리한다.
 */

import { blank, ellipse, line, outline, px, rect, roundRect, squash, type Grid } from './grid';
import type { Palette } from './grid';

export interface MonsterArt {
  frames: Grid[];
  /** 해당 몬스터에만 적용되는 팔레트 덮어쓰기 */
  palette: Palette;
  frameWidth: number;
  frameHeight: number;
}

function eyeBall(g: Grid, cx: number, cy: number, rx: number, ry: number): void {
  ellipse(g, cx, cy, rx, ry, 'q');
  ellipse(g, cx, cy + 0.4, rx * 0.55, ry * 0.55, 'e');
}

function snail(shell: Palette): MonsterArt {
  const build = (f: number): Grid => {
    const g = blank(18, 14);
    const bob = f === 0 ? 0 : 1;
    // 발(몸통)
    roundRect(g, 1, 9, 15, 4, 1, 'm');
    rect(g, 1, 12, 15, 1, 'M');
    // 머리
    ellipse(g, 14, 9, 3, 2.6, 'm');
    // 껍데기
    ellipse(g, 7, 5 + bob, 5.4, 4.8, 'c');
    ellipse(g, 7, 5 + bob, 3.2, 2.9, 'C');
    ellipse(g, 7.5, 5.5 + bob, 1.3, 1.2, 'c');
    // 눈 줄기
    line(g, 13, 7, 13, 3 + bob, 'm');
    px(g, 13, 2 + bob, 'e');
    line(g, 16, 8, 16, 5 + bob, 'm');
    px(g, 16, 4 + bob, 'e');
    // 얼굴
    px(g, 15, 9, 'e');
    return outline(g);
  };
  return { frames: [build(0), build(1)], palette: shell, frameWidth: 18, frameHeight: 14 };
}

function slime(palette: Palette): MonsterArt {
  const base = (): Grid => {
    const g = blank(20, 15);
    ellipse(g, 10, 9, 9, 6, 'n');
    rect(g, 2, 11, 17, 3, 'n');
    rect(g, 2, 13, 17, 1, 'N');
    ellipse(g, 6, 5, 3, 1.8, 'x');
    eyeBall(g, 7, 8, 2, 2.6);
    eyeBall(g, 14, 8, 2, 2.6);
    line(g, 9, 12, 12, 12, 'e');
    px(g, 8, 11, 'e');
    px(g, 13, 11, 'e');
    return outline(g);
  };
  const g0 = base();
  return {
    frames: [g0, squash(g0, 2)],
    palette,
    frameWidth: 20,
    frameHeight: 15,
  };
}

function mushroom(opts: { palette: Palette; horns: boolean }): MonsterArt {
  const build = (f: number): Grid => {
    const g = blank(22, 19);
    const bob = f === 0 ? 0 : 1;
    // 다리
    if (f === 0) {
      rect(g, 6, 16, 4, 2, 'M');
      rect(g, 12, 16, 4, 2, 'M');
    } else {
      rect(g, 5, 16, 4, 2, 'M');
      rect(g, 13, 16, 4, 2, 'M');
    }
    // 몸통
    roundRect(g, 7, 10 + bob, 8, 7, 1, 'm');
    rect(g, 7, 15 + bob, 8, 1, 'M');
    // 뿔
    if (opts.horns) {
      rect(g, 4, 1 + bob, 2, 3, 'B');
      rect(g, 16, 1 + bob, 2, 3, 'B');
      px(g, 4, 0 + bob, 'B');
      px(g, 17, 0 + bob, 'B');
    }
    // 갓
    ellipse(g, 11, 7 + bob, 10, 6.4, 'c');
    rect(g, 1, 7 + bob, 20, 3, 'c');
    rect(g, 2, 10 + bob, 18, 1, 'd');
    ellipse(g, 7, 4 + bob, 3.4, 1.8, 'C');
    // 눈
    rect(g, 7, 6 + bob, 2, 3, 'e');
    rect(g, 13, 6 + bob, 2, 3, 'e');
    px(g, 7, 6 + bob, 'q');
    px(g, 13, 6 + bob, 'q');
    // 입
    line(g, 10, 9 + bob, 12, 9 + bob, 'e');
    return outline(g);
  };
  return { frames: [build(0), build(1)], palette: opts.palette, frameWidth: 22, frameHeight: 19 };
}

function pig(opts: { ribbon: boolean; palette?: Palette }): MonsterArt {
  const build = (f: number): Grid => {
    const g = blank(24, 17);
    const bob = f === 0 ? 0 : 1;
    const legs = f === 0 ? [4, 8, 13, 17] : [3, 9, 12, 18];
    legs.forEach((lx) => {
      rect(g, lx, 12, 3, 4, 'C');
      rect(g, lx, 15, 3, 1, 'o');
    });
    px(g, 1, 7, 'C');
    px(g, 0, 8, 'C');
    px(g, 1, 9, 'C');
    ellipse(g, 10, 9 - bob, 8.5, 5, 'c');
    ellipse(g, 9, 7 - bob, 5, 2.4, 'd');
    ellipse(g, 18, 9 - bob, 4.6, 4.2, 'c');
    px(g, 16, 4 - bob, 'C');
    rect(g, 16, 5 - bob, 2, 2, 'C');
    px(g, 20, 4 - bob, 'C');
    rect(g, 20, 5 - bob, 2, 2, 'C');
    rect(g, 21, 9 - bob, 3, 3, 'd');
    px(g, 22, 10 - bob, 'e');
    px(g, 23, 10 - bob, 'e');
    rect(g, 18, 7 - bob, 2, 2, 'e');
    px(g, 18, 7 - bob, 'q');
    if (opts.ribbon) {
      rect(g, 17, 2 - bob, 4, 2, 'r');
      px(g, 16, 3 - bob, 'R');
      px(g, 21, 3 - bob, 'R');
      px(g, 19, 1 - bob, 'r');
    }
    return outline(g);
  };
  return {
    frames: [build(0), build(1)],
    palette: opts.palette ?? { c: '#f4a0b4', C: '#d67f96', d: '#ffc9d6', r: '#e8503c', R: '#a52d28' },
    frameWidth: 24,
    frameHeight: 17,
  };
}

function stump(palette?: Palette): MonsterArt {
  const build = (f: number): Grid => {
    const g = blank(22, 18);
    const bob = f === 0 ? 0 : 1;
    rect(g, 5, 8 + bob, 12, 8, 'c');
    rect(g, 5, 14 + bob, 12, 2, 'C');
    ellipse(g, 11, 8 + bob, 7.5, 4.2, 'c');
    ellipse(g, 11, 7 + bob, 5, 2.2, 'd');
    rect(g, 4, 16, 3, 2, 'C');
    rect(g, 15, 16, 3, 2, 'C');
    if (f === 1) {
      rect(g, 3, 16, 3, 2, 'C');
      rect(g, 16, 16, 3, 2, 'C');
    }
    rect(g, 7, 9 + bob, 2, 3, 'e');
    rect(g, 13, 9 + bob, 2, 3, 'e');
    px(g, 7, 9 + bob, 'q');
    px(g, 13, 9 + bob, 'q');
    line(g, 9, 13 + bob, 13, 13 + bob, 'e');
    return outline(g);
  };
  return {
    frames: [build(0), build(1)],
    palette: palette ?? { c: '#8f6236', C: '#5b3a1e', d: '#c99510' },
    frameWidth: 22,
    frameHeight: 18,
  };
}

export const MONSTER_ART: Record<string, () => MonsterArt> = {
  'mob-snail': () => snail({ c: '#c4552f', C: '#e8894c', m: '#f7e3bf', M: '#d4bb90' }),
  'mob-blue-snail': () => snail({ c: '#3f76c4', C: '#74a8e8', m: '#e9f0f7', M: '#b9c6d6' }),
  'mob-slime': () => slime({ n: '#7ad46a', N: '#3f9a45' }),
  'mob-blue-slime': () => slime({ n: '#5aa8e0', N: '#2f6eab' }),
  'mob-orange-mushroom': () =>
    mushroom({
      horns: false,
      palette: { c: '#ef8b2c', C: '#ffb761', d: '#b35f16', m: '#f7e0bd', M: '#d2b68c' },
    }),
  'mob-horny-mushroom': () =>
    mushroom({
      horns: true,
      palette: { c: '#8f6fbf', C: '#b699e0', d: '#5d4383', m: '#e7ddf5', M: '#bfb2d4' },
    }),
  'mob-pig': () => pig({ ribbon: false }),
  'mob-ribbon-pig': () => pig({ ribbon: true }),
  'mob-stump': () => stump(),
  'mob-green-mushroom': () =>
    mushroom({
      horns: false,
      palette: { c: '#4caf50', C: '#81c784', d: '#2e7d32', m: '#efe8c8', M: '#c8bc90' },
    }),
  'mob-zombie-mushroom': () =>
    mushroom({
      horns: false,
      palette: { c: '#6b7a3a', C: '#9aaa58', d: '#3a4420', m: '#c8c8a0', M: '#8a8a68' },
    }),
  'mob-jr-necki': () =>
    pig({
      ribbon: false,
      palette: { c: '#6a9a48', C: '#3e6a28', d: '#b8d080', r: '#e8503c', R: '#a52d28' },
    }),
  'mob-curse-eye': () => slime({ n: '#8b3a9a', N: '#4a1e58' }),
  'mob-lupin': () =>
    pig({
      ribbon: true,
      palette: { c: '#c47838', C: '#8a4a18', d: '#e8b070', r: '#e8c030', R: '#a08018' },
    }),
  'mob-stone-bug': () => stump({ c: '#7a7a78', C: '#4a4a48', d: '#c0c0b0' }),
  'mob-star-pixie': () =>
    mushroom({
      horns: false,
      palette: { c: '#f4d15a', C: '#fff3a8', d: '#c9a020', m: '#fff8e0', M: '#e8d9a8' },
    }),
  'mob-luna-pixie': () =>
    mushroom({
      horns: false,
      palette: { c: '#7ec8f0', C: '#c8e8ff', d: '#3a78b0', m: '#f0f6ff', M: '#c0d0e0' },
    }),
  'mob-jr-yeti': () => stump({ c: '#e8eef4', C: '#a8b8c8', d: '#ffffff' }),
  'mob-pepe': () =>
    pig({
      ribbon: false,
      palette: { c: '#4aa0d0', C: '#2a6088', d: '#a8d8f0', r: '#e8503c', R: '#a52d28' },
    }),
  'mob-tick-tock': () =>
    mushroom({
      horns: true,
      palette: { c: '#c9a227', C: '#e8c848', d: '#7a5a10', m: '#d8c8a0', M: '#a09070' },
    }),
  'mob-clock': () => stump({ c: '#c8a040', C: '#7a5818', d: '#f0d878' }),
  'mob-bone-fish': () => snail({ c: '#e8e0d0', C: '#f8f4ea', m: '#c8c0b0', M: '#908878' }),
  'mob-rickel': () => slime({ n: '#3ad0c8', N: '#1a7a78' }),
  'mob-berserkie': () =>
    pig({
      ribbon: true,
      palette: { c: '#6a2820', C: '#3a1010', d: '#a04830', r: '#c02018', R: '#701010' },
    }),
  'mob-dark-wyvern': () => stump({ c: '#2a2038', C: '#100818', d: '#5a4080' }),
};
